import React, { useEffect, useState } from 'react';
import {
  X,
  Loader2,
  Cloud,
  Star,
  Check,
  Sparkles,
  CloudSun,
  Eye,
  RotateCcw,
  Undo2,
} from 'lucide-react';
import { SKY_STYLES, SkyStyle } from '../services/gemini';
import { getSkyStyleDefault, setSkyStyleDefault } from '../services/preferences';

interface BlueSkyEditorProps {
  imageUrl: string;
  /** Generate a new sky for preview (free), returns a data: URL. */
  onGenerate: (style: SkyStyle) => Promise<string>;
  /** Persist the previewed result the user accepted. */
  onAccept: (resultUrl: string, style: SkyStyle) => Promise<void>;
  onCancel: () => void;
}

const BlueSkyEditor: React.FC<BlueSkyEditorProps> = ({
  imageUrl,
  onGenerate,
  onAccept,
  onCancel,
}) => {
  const [style, setStyle] = useState<SkyStyle>(() => getSkyStyleDefault());
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [savedDefault, setSavedDefault] = useState(false);

  // Review flow: once generated, the result is previewed (not saved) until the
  // user accepts it. `resultStyle` is the style that produced the current result.
  const [result, setResult] = useState<string | null>(null);
  const [resultStyle, setResultStyle] = useState<SkyStyle | null>(null);
  const [busy, setBusy] = useState<'idle' | 'generating' | 'saving'>('idle');
  const [showBefore, setShowBefore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoaded(false);
    setLoadError(false);
    setResult(null);
    setResultStyle(null);
    setError(null);
  }, [imageUrl]);

  const isGenerating = busy === 'generating';
  const isSaving = busy === 'saving';
  const isBusy = busy !== 'idle';

  const handleGenerate = async () => {
    if (isBusy || !loaded) return;
    setError(null);
    setBusy('generating');
    try {
      const url = await onGenerate(style);
      setResult(url);
      setResultStyle(style);
      setShowBefore(false);
    } catch (e: any) {
      setError(e?.message || 'Could not generate the sky. Please try again.');
    } finally {
      setBusy('idle');
    }
  };

  const handleAccept = async () => {
    if (!result || !resultStyle || isBusy) return;
    setError(null);
    setBusy('saving');
    try {
      await onAccept(result, resultStyle);
    } catch (e: any) {
      setError(e?.message || 'Could not save the new sky. Please try again.');
      setBusy('idle');
    }
  };

  const handleRevert = () => {
    if (isBusy) return;
    setResult(null);
    setResultStyle(null);
    setShowBefore(false);
    setError(null);
  };

  const handleSaveDefault = () => {
    setSkyStyleDefault(style);
    setSavedDefault(true);
    window.setTimeout(() => setSavedDefault(false), 1800);
  };

  const displayedUrl = result && !showBefore ? result : imageUrl;
  const styleChanged = result != null && style !== resultStyle;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex flex-col">
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-900/80">
        <div className="flex items-center gap-2">
          <Cloud className="w-5 h-5 text-brand-400" />
          <h2 className="text-white font-semibold">Blue Sky</h2>
          <span className="text-xs text-slate-500 hidden sm:inline ml-1">
            AI finds the windows and paints a realistic sky
          </span>
        </div>
        <button
          onClick={onCancel}
          disabled={isBusy}
          className="p-2 text-slate-400 hover:text-white transition-colors disabled:opacity-40"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Preview */}
        <div className="flex-1 min-h-0 flex items-center justify-center p-4 overflow-auto">
          {loadError ? (
            <div className="text-center max-w-sm">
              <p className="text-red-400 font-medium mb-2">
                Failed to load the image for editing.
              </p>
              <p className="text-slate-500 text-sm">
                Try downloading the image and re-uploading, or contact support.
              </p>
            </div>
          ) : (
            <div className="relative max-w-full max-h-full">
              {!loaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-400" />
                  <span className="text-sm">Loading image…</span>
                </div>
              )}
              <img
                src={displayedUrl}
                alt="Sky preview"
                onLoad={() => setLoaded(true)}
                onError={() => setLoadError(true)}
                className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-opacity ${
                  loaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
              {/* Result / before badge */}
              {loaded && result && (
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-950/70 text-white border border-slate-700">
                  {showBefore ? 'Original' : 'New sky (preview)'}
                </span>
              )}
              {isGenerating && (
                <div className="absolute inset-0 rounded-lg bg-slate-950/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-center px-6">
                  <Loader2 className="w-9 h-9 animate-spin text-brand-400" />
                  <p className="text-white font-medium">Generating sky…</p>
                  <p className="text-slate-300 text-sm">
                    The AI is finding the windows and painting a new sky. This
                    usually takes 20–40 seconds.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="w-full lg:w-[360px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-slate-900/80 p-4 flex flex-col gap-5 overflow-y-auto">
          {!result ? (
            <div className="rounded-xl border border-brand-500/30 bg-brand-500/5 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-brand-300" />
                <span className="text-sm font-semibold text-white">
                  Generate sky (AI)
                </span>
                <span className="ml-auto text-[11px] font-medium text-emerald-300">
                  Free
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Automatically detects the windows and open sky, then paints a
                clean, realistic sky. You can review and revert before saving.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-4 h-4 text-emerald-300" />
                <span className="text-sm font-semibold text-white">
                  New sky ready
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Accept to save it, or revert to the original windows and try a
                different style.
              </p>
            </div>
          )}

          {/* Sky style */}
          <div>
            <label className="text-sm font-medium text-slate-200 block mb-2">
              Sky style
            </label>
            <div className="grid grid-cols-1 gap-2">
              {SKY_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  disabled={isBusy}
                  className={`flex items-center justify-between px-3 py-2.5 text-left rounded-lg border transition-colors disabled:opacity-50 ${
                    style === s.id
                      ? 'border-brand-500 bg-brand-500/15 text-white'
                      : 'border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-300'
                  }`}
                >
                  <span className="text-sm font-medium">{s.label}</span>
                  <span className="text-[11px] text-slate-400">{s.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Primary actions */}
          {!result ? (
            <>
              <button
                onClick={handleGenerate}
                disabled={isBusy || !loaded}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating sky…
                  </>
                ) : (
                  <>
                    <CloudSun className="w-4 h-4" />
                    Generate sky with AI
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                onClick={handleAccept}
                disabled={isBusy}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Accept &amp; save
                  </>
                )}
              </button>

              <button
                onMouseDown={() => setShowBefore(true)}
                onMouseUp={() => setShowBefore(false)}
                onMouseLeave={() => setShowBefore(false)}
                onTouchStart={() => setShowBefore(true)}
                onTouchEnd={() => setShowBefore(false)}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                  showBefore
                    ? 'bg-brand-500 text-white'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                }`}
              >
                <Eye className="w-4 h-4" />
                Hold to compare original
              </button>

              <button
                onClick={handleGenerate}
                disabled={isBusy}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4" />
                {styleChanged
                  ? `Regenerate as ${
                      SKY_STYLES.find((s) => s.id === style)?.label
                    }`
                  : 'Try another'}
              </button>

              <button
                onClick={handleRevert}
                disabled={isBusy}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors disabled:opacity-50"
              >
                <Undo2 className="w-4 h-4" />
                Revert to original
              </button>
            </div>
          )}

          {/* Save as default */}
          <button
            onClick={handleSaveDefault}
            disabled={isBusy}
            className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors disabled:opacity-50"
          >
            {savedDefault ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                Saved as your default
              </>
            ) : (
              <>
                <Star className="w-4 h-4" />
                Save as my default
              </>
            )}
          </button>
          <p className="text-[11px] text-slate-500 -mt-3">
            Your default style loads automatically next time you open Blue Sky.
          </p>

          <div className="flex-1" />

          {/* Cancel */}
          <div className="pt-2 border-t border-slate-800">
            <button
              onClick={onCancel}
              disabled={isBusy}
              className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors disabled:opacity-50"
            >
              {result ? 'Close without saving' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlueSkyEditor;
